import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import api from '../../services/api';

const EXAMPLE = `/* Applies to every marketing page. Plain CSS, or paste <style>/<link> tags. */
:root { --brand: #1677FF; }
h1, h2 { font-family: 'Inter Tight', sans-serif; }
.btn-primary { background: var(--brand); }`;

// One place for the marketing site's theme: every page loads this CSS.
export function GlobalThemePanel() {
  const [css, setCss] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/super-admin/website/settings').then(({ data }) => setCss(data.global_css || '')).catch(() => toast.error('Failed to load theme'));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/super-admin/website/settings', { global_css: css });
      toast.success('Global theme saved — every page uses it on next load');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  if (css === null) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="max-w-4xl space-y-3" data-testid="global-theme-panel">
      <p className="text-sm text-slate-600">
        This stylesheet loads on every page of the Vyomos marketing site (home, pricing, custom pages and HTML sections).
        Change the look here once and all pages follow. Paste plain CSS, or HTML with <code>&lt;style&gt;</code> / <code>&lt;link&gt;</code> tags (e.g. Google Fonts).
      </p>
      <textarea rows={24} value={css} onChange={(e) => setCss(e.target.value)} spellCheck={false} placeholder={EXAMPLE}
        className="w-full border border-slate-300 rounded px-3 py-2 font-mono text-xs leading-relaxed bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        data-testid="global-theme-css" />
      <button type="button" onClick={save} disabled={saving} data-testid="global-theme-save"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save theme
      </button>
    </div>
  );
}

export default GlobalThemePanel;
